<template>
  <ContentSection
    spacing="none"
    :class="conditionalSpacing"
  >
    <Container indent="lg-but-on-mobile-default">
      <div class="max-w-container-text">
        <!-- TITLE  -->
        <h2
          v-if="data.heading"
          class="mb-4 typo-body-2xl"
          v-html="data.heading"
        />

        <!-- CHART -->
        <div v-if="data.chart_data" v-animate-in-from-bottom class="overflow-x-auto overflow-y-hidden">
          <div class="aspect-video max-h-[80vh] w-full" :class="{ 'max-lg:min-w-[40rem]': amountDataRows < 100, 'max-lg:min-w-[60rem]': amountDataRows >= 100 && amountDataRows < 1000, 'max-xl:min-w-container-max': amountDataRows >= 1000 && amountDataRows < 3000, 'max-2xl:min-w-[150rem]': amountDataRows >= 3000 }">
            <VChart
              ref="chartElement"
              :style="{
                maxHeight: '80vh',
                width: '100%',
                height: '100%',
              }"
              :option="option"
            />
          </div>
        </div>
      </div>
    </Container>
  </ContentSection>
</template>

<script lang="ts" setup>
import type { ComponentsBrDetailBrLineChartFragment } from '#gql'

const props = defineProps<{
  data: Enforce<ComponentsBrDetailBrLineChartFragment>
  prevComponent?: string | null
  nextComponent?: string | null
}>()

// Use localeProperties.iso if available, fallback to 'de-CH'
const { localeProperties } = useI18n()
const locale = localeProperties.value?.iso || 'de-CH'

const lineConfigs = computed(() => {
  return props.data.line_config
})

const smooth = computed(() => {
  return props.data.line_smooth ? props.data.line_smooth / 100 : 0.2
})

const amountDataRows = computed(() => {
  return props.data?.chart_data?.length || 0
})

const chartElement = ref<null | HTMLDivElement>(null)

const xAxis = computed(() => {
  if (!Array.isArray(props.data.chart_data) || !props.data.chart_data.length) {
    return []
  }
  return props.data.chart_data.slice(1).map(item => item.cells[0])
})

const series = computed(() => {
  if (
    !Array.isArray(props.data.chart_data)
    || props.data.chart_data.length <= 1
  ) {
    return []
  }

  const headers = props.data.chart_data[0].cells.slice(1) // Extract headers excluding the first column
  const dataRows = props.data.chart_data.slice(1) // Exclude the header row

  // Create a series for each column, excluding the first column
  return headers.map((header, index) => {
    const lines = {
      name: lineConfigs.value[index]?.title || header,
      type: 'line',
      smooth: smooth.value,
      data: dataRows.map((row) => {
        const value = row.cells[index + 1]
        return value ? parseFloat(String(value).replace(/[\s ]/g, '').replace(',', '.').replace('%', '')) : NaN
      }),
      lineStyle: {
        color: lineConfigs.value[index]?.line_color,
        width: lineConfigs.value[index]?.line_width || 2,
        type: lineConfigs.value[index]?.line_type.value || 'solid',
      },
      showAllSymbol: true,
      showSymbol: true,
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: {
        color: props.data.show_data_symbol ? lineConfigs.value[index]?.line_color : 'transparent',
      },
      emphasis: {
        focus: 'series', // Highlight entire series, fade others
      },
    }

    return lines
  })
})

const minValue = computed(() => {
  if (!series.value || !series.value.length) {
    return null
  }

  const minDataValue = Math.min(
    ...series.value.flatMap(s => s.data),
  ) - 1
  if (!minDataValue) {
    return null
  }
  return Math.round(minDataValue)
})

const option = ref<ECOption>({
  textStyle: {
    fontFamily: 'Avenir Next LT Pro, Helvetica, Arial, sans-serif', // Set global font
  },

  grid: {
    left: 0,
    right: 35, // right padding
    top: 15, // top padding
    containLabel: true, // Ensure labels are contained within the grid
  },

  legend: {
    orient: 'horizontal',
    type: 'scroll', // Enable scrolling
    data: series.value.map(s => s?.name || 'undefined'), // Dynamically use series names
    left: 0,
    bottom: 0, // Position at bottom
    textStyle: { // typo-body-lg
      fontSize: '16px', // 14px 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      padding: [0, 30, 5, 4],
    },
    itemGap: 0, // Space between legend items
    itemWidth: 35, // Marker width
    itemHeight: 3, // Marker height (matches 3px border-bottom)
  },

  tooltip: {
    trigger: 'item',
    backgroundColor: '#F3F7FF',
    borderWidth: 0,
    padding: [10, 12],
    extraCssText: 'border-radius: 0; box-shadow: none;',
    formatter: (params) => {
      const { seriesName, value, name, color, seriesIndex } = params
      const lineStyle = series.value[seriesIndex]?.lineStyle || {
        type: 'solid',
        color,
      }
      const lineType = lineStyle.type || 'solid'
      const markerStyle = `
        display: inline-block;
        width: 15px;
        height: 0;
        margin-right: 8px;
        margin-bottom: 4px;
        border-bottom: 3px ${lineType} ${lineStyle.color || color};
        vertical-align: middle;
      `

      const formattedName = useFormatDate(name, locale)
      const marker = `<span style="${markerStyle}"></span>`
      const formattedValue = props.data.unit
        ? props.data.unit_prefix
          ? `${props.data.unit} ${value}`
          : `${value}${props.data.unit}`
        : value
      return `<span class="typo-body-lg"><strong>${formattedName}</strong><br/>${marker}${seriesName}: <strong>${formattedValue}</strong></span>`
    },
  },

  xAxis: {
    type: 'category',
    data: xAxis.value,
    offset: 5,
    axisLabel: {
      fontSize: '16px', // typo-body-lg 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      rotate: props.data.rotate_x_axis_labels ? 55 : 0,
      formatter: (value) => {
        return useFormatDate(value, locale)
      },

      margin: 10,
    },
    boundaryGap: true,
  },

  yAxis: {
    type: 'value',
    min: minValue.value,
    axisLabel: {
      fontSize: '16px', // typo-body-lg 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      padding: [0, 5, 0, 4], // Add padding to the left
      formatter: (value) => {
        if (!props.data.unit) return value
        return props.data.unit_prefix
          ? `${props.data.unit} ${value}`
          : `${value}${props.data.unit}`
      },
    },
    splitLine: {
      show: true, // Enable the horizontal lines
      lineStyle: {
        color: '#CCD7EE', // Line color
        width: 1, // Line width (thin)
        type: 'solid', // Line style: 'solid', 'dashed', 'dotted', etc.
      },
    },
    splitNumber: 10, // Number of splits on the y-axis
  },

  series: series.value,
})

const conditionalSpacing = useConditionalSpacing(
  props.prevComponent,
  props.nextComponent,
)

async function handleResize() {
  if (chartElement.value) {
    chartElement.value.resize() // Resize the chart instance
  }
}

const debouncedHandleResize = useDebounceFn(() => {
  handleResize()
}, 200)

onMounted(() => {
  window.addEventListener('resize', debouncedHandleResize)
})

onBeforeUnmount(() => {
  // Clean up event listener and chart instance
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>
